<template>
  <div class="bg-white grow flex flex-col overflow-y-auto scrollbar-hide">
    <FormKit
      v-if="isReady && formArrayDefinition.length > 0 && formObjectDefinition"
      :id="formId"
      v-slot="{ node }"
      ref="form"
      v-model="vModel"
      type="form"
      form-class="flex flex-col grow relative"
      :disabled="disableForm"
      :actions="false"
      :data="data"
      v-on="events.form"
    >
      <div class="sticky top-0 w-full p-5 z-20 flex bg-primary">
        <slot name="header">
          <div
            class="flex flex-row flex-wrap justify-between items-start gap-5 w-full"
          >
            <div>
              <slot
                name="header-title"
                v-bind="{ resourceTitle: resource?.title }"
              >
                <h1 class="text-lg font-bold">
                  {{ customTitle ?? resource?.title }}
                  {{ type === "Create" ? "aanmaken" : "bewerken" }}
                </h1>
              </slot>
              <AppAlert type="error" v-if="Object.keys(node.context.messages).length > 0">
                <FormKitMessages :sections-schema="{
                message: {
                  $el: 'li',
                  attrs: {
                    class: ''
                  },
                }
              }"/>
              </AppAlert>
              <slot name="header-message"/>
            </div>
            <div class="flex items-center gap-1 text-black">
              <slot name="before-actions"/>
              <slot
                name="header-actions"
                v-bind="{ node, submit: () => submit(vModel) }"
              >
                <AppButton
                  v-if="!hideSave"
                  id="form-save"
                  v-tooltip="'Opslaan (Ctrl+S)'"
                  type="submit"
                  icon="fa-floppy-disk"
                  :disabled="disableSubmit"
                  class="toolbar-split-start"
                  @click="saveAndNew = false;saveAndCreateRow = false;closeOnSubmitSuccess=false;"
                >
                  Opslaan
                </AppButton>
                <AppButton
                  v-if="!hideSaveAndNew"
                  id="form-save-and-new-btn"
                  v-tooltip="'Opslaan en nieuw (Ctrl+Shift+S)'"
                  type="submit"
                  icon="fa-floppy-disk-circle-arrow-right"
                  :disabled="disableSubmit"
                  class="toolbar-split-mid"
                  @click="saveAndNew = true"
                >
                  en nog een aanmaken
                </AppButton>
                <AppButton
                  v-if="showRowCreateSave"
                  v-tooltip="`Opslaan en nieuwe ${resource.title.toLowerCase() } regel aanmaken`"
                  type="submit"
                  icon="fa-floppy-disk-pen"
                  class="toolbar-split-mid"
                  :disabled="disableSubmit"
                  @click="saveAndCreateRow = true"
                >
                  en naar regel aanmaken
                </AppButton>
                <AppButton
                  v-if="!hideSaveAndClose"
                  id="form-save-and-close-btn"
                  v-tooltip="'Opslaan en sluiten (Ctrl+S+F4)'"
                  type="submit"
                  icon="fa-floppy-disk-circle-xmark"
                  :disabled="disableSubmit"
                  class="toolbar-split-end"
                  @click="closeOnSubmitSuccess = true"
                >
                  en sluiten
                </AppButton>
              </slot>
              <slot name="after-actions"/>
            </div>
          </div>
        </slot>
      </div>
      <div
        v-if="formArrayDefinition && formObjectDefinition"
        class="flex flex-row flex-wrap h-full"
      >
        <slot
          v-bind="{
            formArrayDefinition,
            schema: formObjectDefinition as any as RequiredNonNullableObject<T>
          }"
        >
          <div class="flex flex-col flex-wrap gap-5 p-5">
            <template
              v-for="inputSchema in formArrayDefinition"
              :key="inputSchema.name"
            >
              <slot
                :name="inputSchema.name"
                v-bind="{ inputSchema }"
              >
                <FormKitSchema
                  :schema="inputSchema"
                  outer-class="col-span-full"
                />
              </slot>
            </template>
          </div>
        </slot>
      </div>
      <AppDataGridLookupPromise ref="gridLookup"/>
    </FormKit>
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { FormKitNode, submitForm as formkitSubmitForm, reset, setErrors } from "@formkit/core";
import { FormKitMessages } from "@formkit/vue";
import { AxiosError, AxiosResponse } from "axios";
import { reduce } from "lodash";
import { ModelRef, ref } from "vue";
import AppDataGridLookupPromise from "~/components/AppDataGridLookupPromise.vue";
import { translate } from "~/plugins/I18n.ts";
import { useApiResources } from "~/stores/ApiResources";
import { useAppTabs } from "~/stores/useAppTabs";
import { FormKitAutocompleteLookupEvent, RequiredNonNullableObject } from "~/types";
import { getFormName, useForm } from "~/utils/Forms/useForm";
import { isFormSubmissionError } from "~/utils/zod/formSubmissionError";
import { onFormMounted, onInputMounted } from "~utils/Forms/FormUIUtils.ts";
import { refreshGridsOfResource } from "~utils/Grids/RefreshGrid.ts";

const props = withDefaults(
	defineProps<{
		// Used to fetch the form definition
		resourceKey: string;
		type: "Edit" | "Create";
		resourceId?: string;
		resourceUrl?: string;
		modelValue?: T;
		formName?: string;
		formId?: string;
		// Custom submit url
		submitUrl?: string;
		submitText?: string;
		submitIcon?: string;
		submitTooltip?: string;
		customTitle?: string;
		closeOnSubmitSuccess?: boolean;
		disableSubmit?: boolean;
		hideSave?: boolean;
		hideSaveAndNew?: boolean;
		hideSaveAndClose?: boolean;
		showRowCreateSave?: boolean;
		disableForm?: boolean;
		onBeforeSubmit?: (data: T, node: FormKitNode) => Promise<T> | T;
	}>(),
	{
		disableForm: false,
		hideSave: false,
		hideSaveAndNew: false,
		hideSaveAndClose: false,
		showRowCreateSave: false,
		closeOnSubmitSuccess: false,
		formId: Math.random().toString(36),
		onBeforeSubmit: async (data: T) => data,
	},
);
const closeOnSubmitSuccess = ref(props.closeOnSubmitSuccess);
const { disableSubmit, formId } = toRefs(props);

const defaultSaveMenuItems = computed(() => {
	const items = [
		{
			disabled: disableSubmit.value,
			text: props.submitText ?? "Opslaan",
			icon: props.submitIcon ?? "fa-save",
			id: "save",
			action: () => {
				saveAndNew.value = false;
				formkitSubmitForm(formId.value);
			},
		},
	];

	if (props.hideSaveAndNew == false) {
		items.push({
			disabled: disableSubmit.value,
			text: "Opslaan en nieuw",
			icon: "fa-floppy-disk-circle-arrow-right",
			id: "save-and-new",
			action: () => {
				saveAndNew.value = true;
				formkitSubmitForm(formId.value);
			},
		});
	}

	return items;
});

const { error, success } = useToast();
const { setCurrentTabTitle, closeCurrentTab } = useAppTabs();
const { push, previousUrl, replace } = useRouter();
const { findByUri, isReady, findByName } = useApiResources();
const saveAndNew = ref(false);
const saveAndCreateRow = ref(false);
const data = reactive({});
const vModel = defineModel<T>({} as T) as ModelRef<T>;
const resource = findByName(props.resourceKey);

const keys = useMagicKeys();

const watchKeys = whenever(keys.ctrl_s, () => {
	if (keys.ctrl_shift_s) {
		saveAndNew.value = true;
	} else if (keys.f4.value) {
		closeOnSubmitSuccess.value = true;
	}

	formNode.submit();
});

onActivated(() => {
	watchKeys.resume();
});

onDeactivated(() => {
	watchKeys.pause();
});

const formName = props.formName ?? getFormName(resource.name, props.type);
const emit = defineEmits<(e: "submit-success", data: T) => void>();

setCurrentTabTitle(resource.title, props.type === "Create" ? "aanmaken" : "bewerken");

const { formArrayDefinition, formObjectDefinition, submitForm, onSubmitError, onSubmitSuccess } = useForm<T>(
	formName,
	resource,
	props.resourceId,
);

onSubmitError((e) => {
	// TODO: Remove ZOD
	const submissionError = isFormSubmissionError(e);

	if (submissionError.success) {
		const { data } = submissionError;
		setErrors(
			props.formId,
			[data.title || data.message, data.detail || ""],
			reduce(
				data.violations || [],
				(obj, v) => {
					if (!(v.field in obj)) {
						obj[v.field] = [];
					}

					obj[v.field].push(v.message);
					return obj;
				},
				{},
			),
		);
	} else {
		const responseData = (e as AxiosError).response.data;
		if (responseData != undefined && "localizedMessage" in responseData) {
			error(responseData.localizedMessage);
		} else {
			console.log(e);
			error("Er is iets onbekend misgegaan bij het opslaan van het formulier.");
		}
	}
});

onSubmitSuccess(async (res: AxiosResponse) => {
	let id = null;
	if (res.status == 201 && "id" in res.data) {
		id = res.data.id;
	}

	if (saveAndNew.value) {
		if (props.type === "Create") {
			// Reset form state
			saveAndNew.value = false;
			reset(props.formId);
			(document.querySelector("form input") as HTMLInputElement | null)?.focus();
		} else {
			// Edit page, close tab then open new create tab
			await closeCurrentTab(false);
			let url = `/${resource.name}/create`;
			if (props.resourceUrl) {
				url += `?resourceUrl=${props.resourceUrl}`;
			}

			await replace(url);
		}
	} else if (closeOnSubmitSuccess.value) {
		const previousPath = previousUrl.value;
		await closeCurrentTab();
		await push(previousPath);
	} else if (saveAndCreateRow.value) {
		await closeCurrentTab();
		const url = `/${resource.name}-row/create?resourceUrl=/api/${resource.name}/${id ?? props.resourceId}/rows`;
		await replace(url);
	} else if (props.type == "Create" && id != null) {
		await closeCurrentTab();
		let url = `/${resource.name}/update/${id}`;
		if (props.resourceUrl) {
			url += `?resourceUrl=${props.resourceUrl}`;
		}
		await replace(url);
	} else if (props.type == "Edit") {
		// Edit page, close tab then open new create tab
		await closeCurrentTab();
		await replace(`/${resource.name}/update/${props.resourceId}`);
	}

	emit("submit-success", vModel.value);
	success(translate(`entities.${resource.name}`) + " opgeslagen.");

	refreshGridsOfResource(resource.name, id);
});

let formNode: FormKitNode;
const gridLookup = ref<InstanceType<typeof AppDataGridLookupPromise>>();

async function tryLookup(lookupEvent: FormKitAutocompleteLookupEvent) {
	const resource = findByUri(lookupEvent.payload.originalOptionSource);
	try {
		if (!resource) {
			throw new Error(`Resource not found for uri: ${lookupEvent.payload.originalOptionSource}`);
		}

		const result = await gridLookup.value?.start({
			resource,
			gridKey: lookupEvent.payload.gridKey,
			selectionMode: lookupEvent.payload.selectionMode,
			customResourceUrl: lookupEvent.payload.optionsSourceWithDependencies,
			filters: lookupEvent.payload.filters,
		});

		if (result) {
			if (result.length === 1 && lookupEvent.payload.selectionMode === "singleRow") {
				lookupEvent.origin.input(result[0]);
			} else if (result.length >= 1 && lookupEvent.payload.selectionMode === "multiRow") {
				lookupEvent.origin.input(result);
			}
		}
	} catch (e) {
		console.error(e);
	}
}

onCloseTab((e) => {
	if (formNode.context?.state.dirty == true && !confirm("Er zijn onopgeslagen wijzigingen. Wilt u doorgaan?")) {
		e.prevent = true;
	}
});

async function submit(formValues: T) {
	const data = await props.onBeforeSubmit(formValues, null!);
	const submitMethod = props.type === "Create" ? "post" : "put";
	await submitForm({
		data,
		method: submitMethod,
		submitUrl: props.submitUrl || resource.uri,
	});
}

const events = {
	form: {
		node: (e: FormKitNode) => {
			formNode = e;

			console.log(formNode);
			// Remove tabindex from dropdown buttons.
			e.on("child", onInputMounted);

			onFormMounted();
			return e.on("autocompleteLookup.deep", tryLookup);
		},
		submit,
	},
};
</script>

<style scoped></style>
